import { ConstructionOutlined } from "@mui/icons-material";
import app from "../Firebase/app";
import initializeStripe from "./initialiseStripe";

// Production price IDs
const PRICE_IDS = {
  3: "price_1PT0iuFYlaVPK4IAXPtp4wRC",
  6: "price_1PT0iuFYlaVPK4IAXPtp4wRC",
  12: "price_1PT0iuFYlaVPK4IAXPtp4wRC",
  beta: "price_1PT0iuFYlaVPK4IAXPtp4wRC",
};

export async function createCheckoutSession(uid, stripeId, plan) {
  return new Promise(async (resolve, reject) => {
    try {
      async function checkout(price) {
        const firestore = app.firestore();
        
        let unsubscribe;
        let timeout;
        
        try {
          // Create checkout session
          // Don't pass customer field - the extension will create a new customer in live mode
          // This avoids issues where the customer was created in test mode but we're using live keys
          const checkoutData = {
            mode: "payment",
            price: price,
            success_url: `${window.location.origin}/purchaseSuccess`,
            cancel_url: window.location.origin,
            allow_promotion_codes: true,
          };
          
          // Note: Not including customer field - the Firebase Extension will create a new customer
          // in live mode. If the user's stripeId was created in test mode, this avoids the mismatch error.
          console.log("Creating checkout session - extension will create new customer if needed");
          console.log("Checkout data being created:", JSON.stringify(checkoutData, null, 2));
          
          const checkoutSessionRef = await firestore
            .collection("users")
            .doc(uid)
            .collection("checkout_sessions")
            .add(checkoutData);

          console.log("Checkout session document created:", checkoutSessionRef.id);
          console.log("Using price ID:", price);

          // Add timeout to prevent infinite waiting
          timeout = setTimeout(() => {
            if (unsubscribe) unsubscribe();
            reject(new Error("Checkout session creation timed out. Please try again or contact support."));
          }, 30000); // 30 second timeout

          unsubscribe = checkoutSessionRef.onSnapshot(async (snap) => {
            if (!snap.exists) {
              console.warn("Checkout session document does not exist");
              return;
            }

            const data = snap.data();
            const { sessionId, error, errorDetails, status } = data || {};
            
            if (error) {
              clearTimeout(timeout);
              unsubscribe();
              // Extract error message properly (could be string or object)
              const errorMessage = typeof error === 'string' 
                ? error 
                : (error.message || error.toString() || 'Unknown error');
              
              // Include error details if available
              let fullErrorMessage = `Checkout session error: ${errorMessage}`;
              if (errorDetails) {
                fullErrorMessage += ` (Type: ${errorDetails.type || 'unknown'}, Code: ${errorDetails.code || 'unknown'})`;
              }
              
              console.error("Checkout session failed:", {
                error: errorMessage,
                errorDetails,
                status,
                data: data
              });
              
              reject(new Error(fullErrorMessage));
              return;
            }

            if (sessionId) {
              clearTimeout(timeout);
              unsubscribe();
              try {
                const stripe = await initializeStripe();
                await stripe.redirectToCheckout({ sessionId });
                resolve();
              } catch (stripeError) {
                reject(new Error(`Stripe redirect failed: ${stripeError.message}`));
              }
            }
          }, (error) => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(`Firestore listener error: ${error.message}`));
          });
        } catch (error) {
          clearTimeout(timeout);
          if (unsubscribe) unsubscribe();
          reject(new Error(`Failed to create checkout session: ${error.message}`));
        }
      }

      // Select price ID based on plan
      let priceId;
      
      switch (plan) {
        case 3:
          priceId = PRICE_IDS[3];
          await checkout(priceId);
          break;
        case 6:
          priceId = PRICE_IDS[6];
          await checkout(priceId);
          break;
        case 12:
          priceId = PRICE_IDS[12];
          await checkout(priceId);
          break;
        case "beta":
          priceId = PRICE_IDS.beta;
          await checkout(priceId);
          break;
        default:
          reject(new Error(`Invalid plan: ${plan}`));
          break;
      }
    } catch (error) {
      reject(error);
    }
  });
}
